import { useEffect } from 'react';
import { router, useLocalSearchParams } from 'expo-router';
import { useUIStore } from '../store/uiStore';

/**
 * Consume the insurer deep-link params on the map route:
 * `/?property=<structureId>&model=<checklistId>`. Insurers mail-merge these
 * links to policyholders; the link lands on the map (big picture first) and
 * primes uiStore.pendingProperty so the Property tab and MapPathsOverlay's
 * Property card open that structure with the insurer's model preselected.
 * Mounted by app/(tabs)/index.tsx alongside useMapFocus, which owns the
 * separate ?focusStructureId channel (share links that pan the map).
 *
 * `?model=` without `?property=` primes uiStore.pendingModel instead.
 *
 * Params are cleared after intake, same as useMapFocus — the state now
 * lives in the store (and sessionStorage on web, so the sign-up round trip
 * keeps it through a reload).
 */
export function usePropertyLinkIntake(): void {
  const { property, model } = useLocalSearchParams<{ property?: string; model?: string }>();
  useEffect(() => {
    if (!property) {
      // `?model=` alone: no home yet, but the insurer's checklist is chosen.
      if (!model) return;
      useUIStore.getState().setPendingModel(model);
      router.setParams({ model: undefined });
      return;
    }
    useUIStore.getState().setPendingProperty({
      structureId: property,
      checklistId: model || undefined,
    });
    router.setParams({ property: undefined, model: undefined });
  }, [property, model]);
}
